import { PencilAltIcon, TrashIcon } from "@heroicons/react/solid";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import spinner from "../../assets/images/spinner.gif";
import useReadWithGet from "../../hooks/read/useReadWithGetMethod";
import LeaveBalance from "./LeaveBalance";
import Pagination from "../../components/pagination";
import LeaveEncashmentModal from "../../components/LeaveEncashmentModal";
import useLeaveEncashment from "../../hooks/leave_encashment/useLeaveEncashment";
import toast from "react-hot-toast";
import Modals from "../../components/modal/Modals";

function LeaveDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Modal states
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    employee_leave_encashment_id: "",
    encashment_type: "",
    encashment_action: "",
    encashment_count: "",
    remarks: "",
  });

  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);

  // Custom hook for leave encashment operations
  const {
    fetchEncashmentDetails,
    createOrUpdateEncashment,
    deleteEncashment,
    isLoading,
    isEditLoading,
    isDeleteLoading,
  } = useLeaveEncashment();

  // Initialize leave encashment listing
  const leaveEncashmentInitialState = {
    month_year: searchParams.get("month_year_leave_balance")
      ? new Date(searchParams.get("month_year_leave_balance"))
      : new Date(),
    user_id: id,
    page_number: 0,
    limit: 10,
  };

  const {
    list: leaveEncashmentList,
    paramsObject,
    setGetListParams,
  } = useReadWithGet({
    url: "leave_encashment_listing",
    initialData: leaveEncashmentInitialState,
  });

  // Handle pagination
  const handlePageClick = (val) => {
    let data = {
      ...paramsObject,
      page_number: val.selected,
    };
    setGetListParams(data);
    setSearchParams(data);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Open modal for creating new encashment
  const handleAddNew = () => {
    setIsEditing(false);
    setFormData({
      employee_leave_encashment_id: "",
      encashment_type: "",
      encashment_action: "",
      encashment_count: "",
      remarks: "",
    });
    setIsOpenModal(true);
  };

  // Open modal for editing encashment
  const handleEdit = async (id) => {
    // Reset form data and set editing mode
    setFormData({
      employee_leave_encashment_id: id,
      encashment_type: "",
      encashment_action: "",
      encashment_count: "",
      remarks: "",
    });

    setIsEditing(true);
    setIsOpenModal(true);

    // Fetch existing encashment details
    try {
      const data = await fetchEncashmentDetails(id);
      if (data) {
        setFormData({
          employee_leave_encashment_id: data.employee_leave_encashment_id,
          encashment_type: data.encashment_type,
          encashment_action: data.encashment_action,
          encashment_count: data.encashment_count,
          remarks: data.remarks || "",
        });
      }
    } catch (error) {
      toast.error("Failed to fetch encashment details.");
    }
  };

  const defaultErrorsValue = {
    encashment_count: "",
  };
  const [errors, setErrors] = useState(defaultErrorsValue);

  const validateEncashmentForm = (formData) => {
    const errorsObj = { ...errors };
    let isValid = true;

    const count = formData.encashment_count;
    if (
      count === "" ||
      isNaN(Number(count)) ||
      Number(count) < 0 ||
      Number(count) % 0.5 !== 0
    ) {
      errorsObj.encashment_count =
      "Please enter a valid count (0, 0.5, 1, 1.5, ...)";
      isValid = false;
    } else {
      errorsObj.encashment_count = "";
    }

    setErrors(errorsObj);

    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!validateEncashmentForm(formData)) {
        toast.error("Please fill all fields correctly");
        return;
      }

      await createOrUpdateEncashment(
        formData,
        id,
        paramsObject.month_year,
        isEditing
      );
      setIsOpenModal(false);
      toast.success(
        isEditing
          ? "Encashment updated successfully!"
          : "Encashment added successfully!"
      );
    } catch (error) {
      toast.error(error.message || "Failed to save encashment.");
    }
  };

  // Show delete confirmation modal
  const handleShowDeleteConfirm = (id) => {
    setDeleteItemId(id);
    setShowDeleteModal(true);
  };

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    try {
      await deleteEncashment(deleteItemId);
      toast.success("Encashment deleted successfully!");
      setShowDeleteModal(false);
    } catch (error) {
      toast.error(error.message || "Failed to delete encashment.");
      setShowDeleteModal(false);
    }
  };

  // Update leave encashment when month_year_leave_balance changes in URL
  useEffect(() => {
    const newMonthYear = searchParams.get("month_year_leave_balance")
      ? new Date(searchParams.get("month_year_leave_balance"))
      : new Date();

    if (paramsObject.month_year.toString() !== newMonthYear.toString()) {
      setGetListParams({
        ...paramsObject,
        month_year: newMonthYear,
      });
    }
  }, [searchParams.get("month_year_leave_balance")]);

  return (
    <Layout>
      <div className="container px-4">
        <div className="text-start">
          <button
            onClick={() => navigate(-1)}
            className="bg-[color:var(--color1)] text-white rounded px-4 py-2"
          >
            Back
          </button>
        </div>
        <LeaveBalance />

        {/* TODO: Uncomment when feature is to be shipped */}
        {leaveEncashmentList.isLoading ? (
          <div className="w-12 h-12 mx-auto mt-10">
            <img src={spinner} alt="spinner" className="w-full h-full" />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-end mb-4">
              <button
                onClick={handleAddNew}
                className="bg-[color:var(--color1)] text-white rounded px-4 py-2"
              >
                Add Leave Adjustment
              </button>
            </div>

            {!leaveEncashmentList.data?.data ||
            leaveEncashmentList.data?.data.length <= 0 ? (
              <div className="text-center">
                <p className="text-gray-700 text-3xl mb-10">
                  No Leave Adjustments To Show
                </p>
              </div>
            ) : (
              <div className="max-w-full overflow-x-auto mt-8">
                <table className="table-auto w-full">
                  <thead>
                    <tr className="bg-[color:var(--color1)] text-center">
                      <th className="w-1/6 min-w-[80px] text-lg font-semibold text-white py-3 lg:py-3 px-3 lg:px-4 border-l border-transparent">
                        Sr.no
                      </th>
                      <th className="w-1/6 min-w-[160px] text-lg font-semibold text-white py-3 lg:py-3 px-3 lg:px-4 border-l border-transparent">
                        Type
                      </th>
                      <th className="w-1/6 min-w-[160px] text-lg font-semibold text-white py-3 lg:py-3 px-3 lg:px-4 border-l border-transparent">
                        Action
                      </th>
                      <th className="w-1/6 min-w-[160px] text-lg font-semibold text-white py-3 lg:py-3 px-3 lg:px-4">
                        Count
                      </th>
                      <th className="w-1/6 min-w-[160px] text-lg font-semibold text-white py-3 lg:py-3 px-3 lg:px-4">
                        Created At
                      </th>
                      <th className="w-1/6 min-w-[160px] text-lg font-semibold text-white py-3 lg:py-3 px-3 lg:px-4">
                        Remarks
                      </th>
                      <th className="w-1/6 min-w-[160px] text-lg font-semibold text-white py-3 lg:py-3 px-3 lg:px-4">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaveEncashmentList.data?.data.map((item, idx) => (
                      <tr key={idx}>
                        <td className="text-center text-dark font-medium text-base py-3 px-2 bg-[#F3F6FF] border-b border-l border-[#E8E8E8]">
                          {idx + 1}
                        </td>
                        <td className="text-center text-dark font-medium text-base py-3 px-2 bg-[#F3F6FF] border-b border-l border-[#E8E8E8]">
                          {item.encashment_type === "encashment"
                            ? "Leave Encashment"
                            : "Others"}
                        </td>
                        <td className="text-center text-dark font-medium text-base py-3 px-2 bg-[#F3F6FF] border-b border-l border-[#E8E8E8]">
                          <span
                            className={
                              item.encashment_action === "ADD"
                                ? "text-green-600"
                                : "text-red-600"
                            }
                          >
                            {item.encashment_action === "ADD"
                              ? "Add"
                              : "Subtract"}
                          </span>
                        </td>
                        <td className="text-center text-dark font-medium text-base py-3 px-2 bg-[#F3F6FF] border-b border-l border-[#E8E8E8]">
                          {item.encashment_count}
                        </td>
                        <td className="text-center text-dark font-medium text-base py-3 px-2 bg-[#F3F6FF] border-b border-l border-[#E8E8E8]">
                          {new Date(item.created_at).toLocaleDateString(
                            "en-GB",
                            {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                            }
                          )}
                        </td>
                        <td className="text-center text-dark font-medium text-base py-3 px-2 bg-[#F3F6FF] border-b border-l border-[#E8E8E8]">
                          {item.remarks || "---"}
                        </td>
                        <td className="text-center text-dark font-medium text-base py-3 px-2 bg-[#F3F6FF] border-b border-l border-[#E8E8E8]">
                          <div className="flex items-center justify-center h-full">
                            <button
                              onClick={() =>
                                handleEdit(item.employee_leave_encashment_id)
                              }
                              className="text-blue-600 mx-1"
                              disabled={isEditLoading}
                            >
                              <PencilAltIcon className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() =>
                                handleShowDeleteConfirm(
                                  item.employee_leave_encashment_id
                                )
                              }
                              className="text-red-600 mx-1"
                              disabled={isDeleteLoading}
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-5 mt-5">
                  <Pagination
                    currentPage={+paramsObject.page_number}
                    lengthofItems={leaveEncashmentList.data?.total_count}
                    limit={paramsObject.limit}
                    onPageChange={handlePageClick}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Leave Encashment Modal */}
      <LeaveEncashmentModal
        isOpen={isOpenModal}
        onClose={() => {
          setIsOpenModal(false);
          setErrors({ ...defaultErrorsValue });
        }}
        formData={formData}
        onChange={handleInputChange}
        onSubmit={handleSubmit}
        isEditing={isEditing}
        isLoading={isLoading}
        errors={errors}
        isEditLoading={isEditLoading}
      />

      {/* Delete Confirmation Modal */}
      <Modals show={showDeleteModal}>
        <div className="delete-confirmation">
          <h3 className="text-xl font-bold mb-4">Confirm Delete</h3>
          <p className="mb-6">
            Are you sure you want to delete this leave encashment? This action
            cannot be undone.
          </p>
          <div className="flex justify-end space-x-4">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="py-2 px-4 rounded font-semibold border border-[color:var(--color1)]"
              disabled={isDeleteLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              className="bg-red-600 text-white py-2 px-4 rounded font-semibold flex items-center justify-center"
              disabled={isDeleteLoading}
            >
              {isDeleteLoading ? (
                <>
                  <img src={spinner} alt="Loading" className="w-5 h-5 mr-2" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </button>
          </div>
        </div>
      </Modals>
    </Layout>
  );
}

export default LeaveDetail;
